import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send, CheckCircle } from 'lucide-react';

const ContactPage = () => {
  const [formStatus, setFormStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/xeeeqwvj', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <main className="bg-[#0a0a0a] min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#6a6a6a] text-[13px] mb-3 uppercase tracking-wider">Get In Touch</p>
          <h1 className="text-[48px] md:text-[64px] font-extralight text-white mb-6 tracking-[-0.02em]">
            Contact LV1
          </h1>
          <p className="text-[#8a8a8a] text-[17px] leading-[1.7] max-w-[550px] mx-auto">
            Have a question about our brands or interested in partnering with us? We would love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="text-white text-[28px] font-light mb-8">Contact Information</h2>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-white/60" />
                </div>
                <div>
                  <p className="text-white text-[16px] font-medium mb-1">Headquarters</p>
                  <p className="text-[#8a8a8a] text-[15px] leading-[1.6]">
                    140 Smith St.<br />
                    Keasbey, NJ 08832
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-white/60" />
                </div>
                <div>
                  <p className="text-white text-[16px] font-medium mb-1">Email</p>
                  <a href="mailto:info@lv1group.com" className="text-[#8a8a8a] text-[15px] hover:text-white transition-colors">
                    info@lv1group.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-white/60" />
                </div>
                <div>
                  <p className="text-white text-[16px] font-medium mb-1">Phone</p>
                  <a href="tel:+1-732-738-6800" className="text-[#8a8a8a] text-[15px] hover:text-white transition-colors">
                    (732) 738-6800
                  </a>
                </div>
              </div>
            </div>

            {/* Inquiry Types */}
            <div
              className="p-6 rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <h3 className="text-white text-[16px] font-medium mb-4">We Can Help With</h3>
              <ul className="space-y-2 text-[#8a8a8a] text-[14px]">
                <li>Brand partnership inquiries</li>
                <li>Retail buyer questions</li>
                <li>Showroom visit scheduling</li>
                <li>General business inquiries</li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className="p-8 rounded-2xl"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {formStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-white/80" />
                </div>
                <h3 className="text-white text-[24px] font-light mb-3">Message Sent</h3>
                <p className="text-[#8a8a8a] text-[15px]">
                  Thank you for reaching out. We will get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-white/70 text-[14px] mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-white/10 text-white text-[15px] placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-[14px] mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-white/10 text-white text-[15px] placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-[14px] mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-white/10 text-white text-[15px] placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                    placeholder="Your company (optional)"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-[14px] mb-2">Inquiry Type</label>
                  <select
                    name="inquiry_type"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-white/10 text-white text-[15px] focus:outline-none focus:border-white/30 transition-colors appearance-none cursor-pointer"
                    defaultValue=""
                  >
                    <option value="" disabled className="bg-[#0a0a0a]">Select inquiry type</option>
                    <option value="Brand Partnership" className="bg-[#0a0a0a]">Brand Partnership</option>
                    <option value="Retail Buyer Inquiry" className="bg-[#0a0a0a]">Retail Buyer Inquiry</option>
                    <option value="Showroom Visit" className="bg-[#0a0a0a]">Showroom Visit</option>
                    <option value="General Inquiry" className="bg-[#0a0a0a]">General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/70 text-[14px] mb-2">Message</label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-white/10 text-white text-[15px] placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                    placeholder="Tell us about your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-[#0a0a0a] rounded-full text-[15px] font-medium hover:bg-white/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>

                {formStatus === 'error' && (
                  <p className="text-red-400 text-[14px] text-center">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
